import {
  GraphQLInputObjectType,
  GraphQLNonNull,
  GraphQLList,
  GraphQLString,
  GraphQLID,
  GraphQLInputFieldConfigMap
} from 'graphql'
import {
  ApplicationFilterArgType,
  ApplicationFilterArg
} from './application-filter-arg'
import { ServerFilterArg, ServerFilterArgType } from './server-filter-arg'
import {
  VulnerabilityFilterArg,
  vulnerabilityFilterArgType
} from './vulnerability-filter-arg'
import { LibraryFilterArg, libraryFilterArgType } from './library-filter-arg'

export interface TagFilterMutationArgs<TInput> {
  readonly input: TInput
}

export interface TagFilterMutationInputArgs<TFilter> {
  readonly add: string[]
  readonly remove: string[]
  readonly filters: TFilter
  readonly organizationID: string
}

function getFields(
  filterArgType: GraphQLInputObjectType
): GraphQLInputFieldConfigMap {
  return {
    organizationID: {
      type: new GraphQLNonNull(GraphQLID),
      description:
        'The ID of the organization that owns the entities being tagged or untagged.'
    },
    add: {
      type: new GraphQLList(new GraphQLNonNull(GraphQLString)),
      description: 'A list of tags to add to the entities'
    },
    remove: {
      type: new GraphQLList(new GraphQLNonNull(GraphQLString)),
      description: 'A list of tags to remove from the entities'
    },
    filters: {
      description:
        'A collection of filters to specify which entities are being tagged or untagged.',
      type: new GraphQLNonNull(filterArgType)
    }
  }
}

export interface TagApplicationsByFiltersInputArgs
  extends TagFilterMutationInputArgs<ApplicationFilterArg> {
  readonly excludedApplications: string[]
}

export type TagApplicationsByFiltersArg =
  TagFilterMutationArgs<TagApplicationsByFiltersInputArgs>

export const tagApplicationsByFiltersArg = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'TagApplicationsByFiltersArg',
      description:
        'Mutation input type for updating application tags based on a set of filters.',
      fields: () => ({
        ...getFields(ApplicationFilterArgType),
        excludedApplications: {
          description:
            'A list of application IDs to exclude from being tagged or untagged.',
          type: new GraphQLList(new GraphQLNonNull(GraphQLString))
        }
      })
    })
  }
}

export interface TagServersByFiltersInputArgs
  extends TagFilterMutationInputArgs<ServerFilterArg> {
  readonly excludedServers: string[]
}

export type TagServersByFiltersArg =
  TagFilterMutationArgs<TagServersByFiltersInputArgs>

export const tagServersByFiltersArg = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'TagServersByFiltersArg',
      description:
        'Mutation input type for updating server tags based on a set of filters.',
      fields: () => ({
        ...getFields(ServerFilterArgType),
        excludedServers: {
          description:
            'A list of server IDs to exclude from being tagged or untagged.',
          type: new GraphQLList(new GraphQLNonNull(GraphQLString))
        }
      })
    })
  }
}

export interface TagVulnerabilitiesByFiltersInputArgs
  extends TagFilterMutationInputArgs<VulnerabilityFilterArg> {
  readonly excludedVulnerabilities: string[]
}

export type TagVulnerabilitiesByFiltersArg =
  TagFilterMutationArgs<TagVulnerabilitiesByFiltersInputArgs>

export const tagVulnerabilitiesByFiltersArg = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'TagVulnerabilitiesByFiltersArg',
      description:
        'Mutation input type for updating vulnerability tags based on a set of filters.',
      fields: () => ({
        ...getFields(vulnerabilityFilterArgType),
        excludedVulnerabilities: {
          description:
            'A list of vulnerability IDs to exclude from being tagged or untagged.',
          type: new GraphQLList(new GraphQLNonNull(GraphQLString))
        }
      })
    })
  }
}

export interface TagLibrariesByFiltersInputArgs
  extends TagFilterMutationInputArgs<LibraryFilterArg> {
  readonly excludedHashes: string[]
}

export type TagLibrariesByFiltersArg =
  TagFilterMutationArgs<TagVulnerabilitiesByFiltersInputArgs>

export const tagLibrariesByFiltersArg = {
  input: {
    type: new GraphQLInputObjectType({
      name: 'TagLibrariesByFiltersArg',
      description:
        'Mutation input type for updating library tags based on a set of filters.',
      fields: () => ({
        ...getFields(libraryFilterArgType),
        excludedHashes: {
          description:
            'A list of vulnerability IDs to exclude from being tagged or untagged.',
          type: new GraphQLList(new GraphQLNonNull(GraphQLString))
        }
      })
    })
  }
}
